/**
 * Configuración centralizada de rutas
 * 
 * Este archivo contiene toda la información relacionada con las rutas de la aplicación:
 * - Paths de las rutas
 * - Títulos para navegación y breadcrumbs
 * - Permisos requeridos
 * - Datos maestros necesarios
 * 
 * Principio: Single Source of Truth (SSOT) - Una sola fuente de verdad para todas las rutas
 */

import { PERMISOS } from "@/constants/permissions";
import type { BreadcrumbItemType } from "@/components/ui/breadcrumb";

export interface RouteConfig {
  /** Ruta de la aplicación (ej: "/agreements") */
  path: string;
  /** Título para mostrar en el sidebar */
  title: string;
  /** Etiqueta para el breadcrumb */
  breadcrumbLabel: string;
  /** Permiso requerido para acceder a esta ruta */
  permission: string;
  /** Datos maestros necesarios para esta ruta */
  masterData: string[];
  /** Categoría de la ruta (para agrupar en el sidebar) */
  category?: "gestión" | "negociaciones" | "maestros";
}

/**
 * Configuración de todas las rutas de la aplicación
 */
export const ROUTES: Record<string, RouteConfig> = {
  // Gestión
  approvals: {
    path: "/approvals",
    title: "Bandeja aprobaciones",
    breadcrumbLabel: "Gestión de negociaciones",
    permission: PERMISOS.COMPRAS.VER_APROBACIONES,
    masterData: ["tipo_rechazos", "aprobadores", "estados_negociacion"],
    category: "gestión",
  },
  invoices: {
    path: "/invoices",
    title: "Documentos",
    breadcrumbLabel: "Documentos",
    permission: PERMISOS.COMPRAS.VER_GESTION,
    masterData: ["laboratorios"],
    category: "gestión",
  },
  groups: {
    path: "/groups",
    title: "Grupo laboratorios",
    breadcrumbLabel: "Negociación de grupos",
    permission: PERMISOS.NEGOCIACIONES.VER_GRUPOS,
    masterData: ["sociedades", "productos", "laboratorios", "aprobadores"],
    category: "gestión",
  },

  // Negociaciones
  agreements: {
    path: "/agreements",
    title: "Acuerdos",
    breadcrumbLabel: "Negociación de acuerdos",
    permission: PERMISOS.NEGOCIACIONES.VER_GRUPO_ESCALAS,
    masterData: [
      "sociedades",
      "aprobadores",
      "grupos",
      "escalas",
      "descuentos",
      "tipo_descuentos",
      "bases",
      "grupo_descuentos",
      "beneficiarios",
      "tipo_base",
      "estados_negociacion",
      "periodos",
    ],
    category: "negociaciones",
  },
  growth: {
    path: "/growth",
    title: "Crecimiento",
    breadcrumbLabel: "Negociaciones de crecimiento",
    permission: PERMISOS.NEGOCIACIONES.VER_CRECIMIENTO,
    masterData: [
      "sociedades",
      "aprobadores",
      "grupos",
      "escalas",
      "descuentos",
      "tipo_descuentos",
      "bases",
      "grupo_descuentos",
      "beneficiarios",
      "tipo_base",
      "estados_negociacion",
      "periodos",
    ],
    category: "negociaciones",
  },
  effectivenessSupplies: {
    path: "/effectiveness-supplies",
    title: "Efectividad-Suministros",
    breadcrumbLabel: "Negociación de efectividad y suministros",
    permission: PERMISOS.NEGOCIACIONES.VER_ESCALAS_EFECTIVAS,
    masterData: [
      "sociedades",
      "aprobadores",
      "grupos",
      "escalas",
      "descuentos",
      "tipo_descuentos",
      "bases",
      "grupo_descuentos",
      "beneficiarios",
      "tipo_base",
      "estados_negociacion",
      "periodos",
    ],
    category: "negociaciones",
  },

  rotationInventory: {
    path: "/rotation-inventory",
    title: "Rotación e inventario",
    breadcrumbLabel: "Negociación de rotación e inventario",
    permission: PERMISOS.NEGOCIACIONES.VER_UNIFICADOS,
    masterData: [
      "sociedades",
      "aprobadores",
      "grupos",
      "escalas",
      "descuentos",
      "tipo_descuentos",
      "bases",
      "grupo_descuentos",
      "beneficiarios",
      "tipo_base",
      "estados_negociacion",
      "periodos",
    ],
    category: "negociaciones",
  },
  bonuses: {
    path: "/bonuses",
    title: "Bonificaciones",
    breadcrumbLabel: "Negociación de bonificaciones",
    permission: PERMISOS.NEGOCIACIONES.VER_BONIFICACIONES,
    masterData: [
      "sociedades",
      "aprobadores",
      "grupos",
      "escalas",
      "descuentos",
      "tipo_descuentos",
      "bases",
      "grupo_descuentos",
      "beneficiarios",
      "tipo_base",
      "estados_negociacion",
      "periodos",
    ],
    category: "negociaciones",
  },

  // Maestros
  templateRecipients: {
    path: "/maestros/template-recipients",
    title: "Destinatarios de plantillas",
    breadcrumbLabel: "Destinatarios de plantillas",
    permission: PERMISOS.MAESTROS.VER_TEMPLATE_RECIPIENTS,
    masterData: [],
    category: "maestros",
  },
};

/**
 * Obtiene la configuración de una ruta por su path
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
  return Object.values(ROUTES).find((route) => route.path === path);
}

/**
 * Genera el mapa de breadcrumbs desde la configuración de rutas
 */
export function getBreadcrumbMap(): Record<string, BreadcrumbItemType[]> {
  const breadcrumbMap: Record<string, BreadcrumbItemType[]> = {
    "/": [{ label: "Inicio" }, { label: "Documentos" }],
  };

  Object.values(ROUTES).forEach((route) => {
    breadcrumbMap[route.path] = [
      { label: "Inicio", href: "/" },
      { label: route.breadcrumbLabel },
    ];
  });

  return breadcrumbMap;
}

/**
 * Genera el mapa de datos maestros desde la configuración de rutas
 */
export function getMasterDataMap(): Record<string, string[]> {
  const masterDataMap: Record<string, string[]> = {};

  Object.values(ROUTES).forEach((route) => {
    masterDataMap[route.path] = route.masterData;
  });

  return masterDataMap;
}

/**
 * Obtiene todas las rutas de una categoría específica
 */
export function getRoutesByCategory(
  category: "gestión" | "negociaciones" | "maestros"
): RouteConfig[] {
  return Object.values(ROUTES).filter(
    (route) => route.category === category
  );
}

